import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import adminConsoleService from '@services/adminConsoleService';

interface Phase {
  PhaseId: number;
  Name: string;
  isEnable: boolean;
  Disabled: boolean;
}

type ModalMode = 'add' | 'edit' | 'delete';

// ─── Modal ────────────────────────────────────────────────────────────────────

interface PhaseModalProps {
  mode: ModalMode;
  phase: Partial<Phase>;
  saving: boolean;
  error: string | null;
  onChange: (title: string) => void;
  onConfirm: () => void;
  onClose: () => void;
}

const MODAL_TITLES: Record<ModalMode, string> = {
  add: 'Add New Phase',
  edit: 'Edit Phase',
  delete: 'Delete Phase',
};

const PhaseModal: React.FC<PhaseModalProps> = ({
  mode,
  phase,
  saving,
  error,
  onChange,
  onConfirm,
  onClose,
}) =>
{
  const isDelete = mode === 'delete';

  return createPortal(
    <div className="admin-console-portal fixed inset-0 z-[10000] flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-lg shadow-2xl w-[380px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#e0e0e0]">
          <h2 className="text-[16px] font-normal text-[#5697b3]">{MODAL_TITLES[mode]}</h2>
          <button onClick={onClose} className="text-[#bbb] hover:text-[#555]">
            <span className="material-icons text-[18px]">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 flex flex-col gap-3">
          {error && (
            <div className="px-3 py-2 bg-[#fdecea] border border-[#e57373] rounded text-[12px] text-[#c62828]">
              {error}
            </div>
          )}
          <div className="flex flex-col gap-1">
            <label className="text-[12px] text-[#A5A7AC]">Phase Name:</label>
            {isDelete ? (
              <div className="bg-[#f5f5f5] border border-[#e0e0e0] rounded px-3 py-1.5 text-[12px] text-[#555]">
                {phase.Name}
              </div>
            ) : (
              <input
                autoFocus
                type="text"
                value={phase.Name || ''}
                placeholder={mode === 'add' ? 'Add new phase' : ''}
                onChange={e => onChange(e.target.value)}
                onKeyDown={e =>
                {
                  if (e.key === 'Enter') onConfirm();
                }}
                className="border border-[#d0d0d0] rounded px-3 py-1.5 text-[12px] outline-none focus:border-[#62aacb]"
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#e0e0e0]">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-1.5 text-[12px] bg-[#aaa] text-white rounded hover:bg-[#999] transition-colors disabled:opacity-50  dark:bg-[#5c6060] dark:hover:brightness-[0.85]"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={saving}
            className="px-4 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] transition-colors disabled:opacity-50 dark:bg-[#3c6c80] dark:hover:brightness-[0.85]"
          >
            {saving ? '…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ─── Main Tab ─────────────────────────────────────────────────────────────────

export const PhasesManagerTab: React.FC = () =>
{
  const [phases, setPhases] = useState<Phase[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Modal
  const [modal, setModal] = useState<ModalMode | null>(null);
  const [selected, setSelected] = useState<Partial<Phase>>({});
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // Reactivating
  const [reactivatingId, setReactivatingId] = useState<number | null>(null);

  const loadPhases = useCallback(async () =>
  {
    try
    {
      const data = await adminConsoleService.getPhases();
      setPhases(data);
    }
    catch
    {
      // non-fatal
    }
    finally
    {
      setLoading(false);
    }
  }, []);

  useEffect(() =>
  {
    loadPhases();
  }, [loadPhases]);

  const filtered = useMemo(() =>
  {
    const q = search.trim().toLowerCase();
    if (!q) return phases;
    return phases.filter(p => p.Name.toLowerCase().includes(q));
  }, [phases, search]);

  const openAdd = () =>
  {
    setSelected({ Name: '' });
    setModalError(null);
    setModal('add');
  };

  const openEdit = (p: Phase) =>
  {
    setSelected({ ...p });
    setModalError(null);
    setModal('edit');
  };

  const openDelete = (p: Phase) =>
  {
    setSelected({ ...p });
    setModalError(null);
    setModal('delete');
  };

  const closeModal = () =>
  {
    setModal(null);
    setSelected({});
    setModalError(null);
  };

  const handleConfirm = async () =>
  {
    if (!modal) return;
    const title = String(selected.Name ?? '').trim();
    if ((modal === 'add' || modal === 'edit') && !title)
    {
      setModalError('Phase Name is required.');
      return;
    }
    if (phases.some(x => x.Name === title) && modal === 'add')
    {
      setModalError('This phase has already been added.');
      return;
    }
    setSaving(true);
    setModalError(null);
    try
    {
      if (modal === 'add')
      {
        const phaseId =
          Math.max.apply(
            Math,
            filtered.map(function (o)
            {
              return o.PhaseId;
            }),
          ) + 1;
        await adminConsoleService.createPhase(phaseId, title);
      }
      else if (modal === 'edit')
      {
        await adminConsoleService.editPhase(selected.PhaseId!, title);
      }
      else
      {
        await adminConsoleService.deletePhase(selected.PhaseId!, title);
      }
      closeModal();
      setLoading(true);
      await loadPhases();
    }
    catch
    {
      setModalError('Operation failed. Please try again.');
    }
    finally
    {
      setSaving(false);
    }
  };

  const handleReactivate = async (p: Phase) =>
  {
    setReactivatingId(p.PhaseId);
    try
    {
      await adminConsoleService.reactivatePhase(p.PhaseId, p.Name);
      setLoading(true);
      await loadPhases();
    }
    catch
    {
      // ignore
    }
    finally
    {
      setReactivatingId(null);
    }
  };

  return (
    <div className="px-6 py-5">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-[#555] whitespace-nowrap">Find by keyword:</span>
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search phases..."
              className="border border-[#d0d0d0] rounded px-3 py-1 text-[12px] outline-none focus:border-[#62aacb] w-[200px]"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#bbb] hover:text-[#555]"
              >
                <span className="material-icons text-[13px] !leading-10">close</span>
              </button>
            )}
          </div>
        </div>

        <button
          onClick={openAdd}
          className="flex items-center gap-1 text-[12px] text-[#5697b3] hover:text-[#3d7fa8] transition-colors"
        >
          <span className="material-icons text-[18px] dark:text-[#beb7ac]">add_circle_outline</span>
          Add New Phase
        </button>
      </div>

      {/* ── Table ───────────────────────────────────────────────────────── */}
      <div className="border border-[#ddd] rounded-sm bg-white shadow-sm overflow-x-auto">
        {loading ? (
          <div className="flex items-center gap-2 p-5 text-[12px] text-[#aaa]">
            <span className="material-icons animate-spin text-[16px] text-[#62aacb]">refresh</span>
            Loading…
          </div>
        ) : (
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-[#f5f5f5] border-b border-[#e0e0e0]">
                <th className="text-left px-4 py-2 text-[#555] font-medium dark:text-[#beb7ac]">
                  Phase
                </th>
                <th className="text-right px-4 py-2 text-[#555] font-medium w-[160px] dark:text-[#beb7ac]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={2} className="text-center py-6 text-[#aaa]">
                    {search ? 'No results match your search.' : 'No phases found.'}
                  </td>
                </tr>
              ) : (
                filtered.map((p, i) =>
                {
                  const isDeleted = !p.isEnable;
                  return (
                    <tr
                      key={p.PhaseId}
                      className={`border-b border-[#f0f0f0] ${i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'} hover:bg-[#f0f6fb] transition-colors ${isDeleted ? 'opacity-60' : ''}`}
                    >
                      <td className="px-4 py-2 text-[#5697b3] max-w-40 whitespace-nowrap text-ellipsis overflow-hidden">
                        {p.Name}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {isDeleted ? (
                            <button
                              onClick={() => handleReactivate(p)}
                              disabled={reactivatingId === p.PhaseId}
                              className="px-3 py-1 bg-[#aaa] text-white rounded text-[11px] hover:bg-[#888] transition-colors disabled:opacity-50 dark:bg-[#5c6060] dark:hover:brightness-[0.85]"
                            >
                              {reactivatingId === p.PhaseId ? '…' : 'Show'}
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => openEdit(p)}
                                className="px-3 py-1 bg-[#62aacb] text-white rounded text-[11px] hover:bg-[#4e99ba] transition-colors dark:bg-[#3c6c80] dark:hover:brightness-[0.85]"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => openDelete(p)}
                                className="px-3 py-1 bg-[#e57373] text-white rounded text-[11px] hover:bg-[#d32f2f] transition-colors dark:bg-[#8d312c] dark:hover:brightness-[0.85]"
                              >
                                Delete
                              </button>
                              <button
                                onClick={() => handleReactivate(p)}
                                disabled={reactivatingId === p.PhaseId}
                                className="px-3 py-1 bg-[#aaa] text-white rounded text-[11px] hover:bg-[#888] transition-colors disabled:opacity-50 dark:bg-[#5c6060] dark:hover:brightness-[0.85]"
                              >
                                Show
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <PhaseModal
          mode={modal}
          phase={selected}
          saving={saving}
          error={modalError}
          onChange={title => setSelected(prev => ({ ...prev, Name: title }))}
          onConfirm={handleConfirm}
          onClose={closeModal}
        />
      )}
    </div>
  );
};
